import { Injectable } from '@angular/core';
import { AssessmentCreate, AssessmentRestore, AssessmentsService } from "./assessments.service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import {
    BasicEvent,
    Client,
    ClientVisitEvent,
    MultipleAssignees,
} from "../../../../common/src/lib/models";
import { map, shareReplay, switchMap, take } from "rxjs/operators";
import { ScheduleTab } from "../models/navigation.model";
import { CalendarView } from "angular-calendar";
import { NotesService } from './notes.service';
import { JobCreate, JobRestore, JobsService, JobUserRestore } from './jobs.service';
import { BusinessService } from './business.service';
import { ViewAsService } from './view-as.service';
import { PaymentService } from './payment.service';
import { UsersService } from './users.service';
import { LobbyService } from './lobby.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { DateFilter } from '../models/date-filter.model';
import { dateString, MakeOptional } from "../../../../common/src/lib/services";
import { showSnackbar } from "../../../../common/src/lib/components/snackbar/snackbar.component";
import { EstimateCreate, EstimatesService } from "./estimates.service";
import { InvoiceCreate, InvoicesService } from "./invoices.service";
import { LeadRestore, LeadsService, LeadUpdate } from "./leads.service";
import { Job, JobStatus, JobTileData, jobTileData, JobTimeRange } from '../models/jobs.model';
import { DynamicListComponent, DynamicListEvent, Predicate } from '../components/dynamic-list/dynamic-list.component';
import { JobTileComponent } from '../components/job-tile/job-tile.component';
import { TimeRange } from 'projects/common/src/lib/models/time-range.model';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { Workflow } from '../models/workflow.model';
import {
    Assessment,
    AssessmentStatus,
    AssessmentTileData,
    assessmentTileData,
    AssessmentTimeRange
} from '../models/assessment.model';
import { AssessmentTileComponent } from '../components/assessment-tile/assessment-tile.component';
import { Availability, AvailabilityTileData } from '../models/availability.model';
import { AvailabilityTileComponent } from '../components/availability-tile/availability-tile.component';
import { AvailabilityService } from './availability.service';
import { Note } from "../models/note.model";
import { Router } from "@angular/router";
import {
    ConfirmationDialog
} from "../../../../common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { ModalBehavior, ModalsService } from "../../../../common/src/lib/services/modals.service";
import { TimetableDialogComponent } from "../modals/timetable-dialog/timetable-dialog.component";
import { SupabaseService } from "./supabase.service";
import { PaginationOptions } from "./pagination.service";
import { ScheduleItem } from "../models/schedule.model";
import { clientRestoreFromDocument } from "../../../../common/src/lib/models/client-transform";
import { RequestInvitationFormData, RequestInvitationModalData } from "../modals/request-invitation/request-invitation.model";
import moment from 'moment';
import { LeadTileComponent } from '../components/lead-tile/lead-tile.component';
import { Lead, LeadTileData, leadTileData, LeadTimeRange } from 'projects/common/src/lib/models/lead.model';
import { RequestInvitationModal } from '../modals/request-invitation/request-invitation.component';

export type ScheduleTileData = LeadTileData | AssessmentTileData | JobTileData | AvailabilityTileData;

export function sortSchedule(scheduleType: ScheduleTab) {
    return (source$: Observable<BasicEvent[] | null>) => {
        return source$.pipe(
            map(schedule => {
                if (!schedule)
                    return null;
                schedule.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
                return schedule;
            })
        ) as Observable<BasicEvent[] | null>;
    }
}

export function filterEventsByStatus<T extends ClientVisitEvent | MultipleAssignees<ClientVisitEvent>>(sort: (a: T, b: T) => number) {
    return function (source$: Observable<[T[] | null, EventStatusFilter | null]>): Observable<T[] | null> {
        return source$.pipe(
            map(([events, filterStatus]) => {
                if (!events)
                    return null;
                if (!filterStatus) 
                    return events.sort(sort);
                let filteredEvents = events;
                if (filterStatus !== 'All') {
                    filteredEvents = events.filter((event) => event.submitStatus === filterStatus);
                }
                return filteredEvents.sort(sort);
            }),
        );
    }
}
export function scheduleToList(schedule: ScheduleItem[]) {
    return schedule.map(item => {
        let document: Partial<LeadTileData> | Partial<AssessmentTileData> | Partial<JobTileData> | Availability;
        if (item.itemType === 'lead') {
            document = {
                docType: 'Lead',
                id: item.id,
                businessName: item.businessName,
                firstName: item.firstName,
                lastName: item.lastName,
                startTime :item.startTime,
                endTime: item.endTime,
                jobType: item.jobType,
                ownerFirstName: item.ownerFirstName,
                ownerLastName: item.ownerLastName,
                rangeId: item.rangeId,
                phoneNumber: item.phoneNumber,
                workflowId: item.workflowId,
            };
        } else if (item.itemType === 'assessment') {
            document = {
                docType: 'Assessment',
                id: item.id,
                businessName: item.businessName,
                firstName: item.firstName,
                lastName: item.lastName,
                startTime :item.startTime,
                endTime: item.endTime,
                jobType: item.jobType,
                ownerFirstName: item.ownerFirstName,
                ownerLastName: item.ownerLastName,
                rangeId: item.rangeId,
                phoneNumber: item.phoneNumber,
                workflowId: item.workflowId,
                status: item.status as AssessmentStatus,
                invoiceId: item.invoiceId,
                estimateId: item.estimateId,
                jobId: item.jobId,
            };
        } else if (item.itemType === 'job') {
            document = {
                docType: 'Job',
                id: item.id,
                businessName: item.businessName,
                firstName: item.firstName,
                lastName: item.lastName,
                startTime: item.startTime,
                endTime: item.endTime,
                jobType: item.jobType,
                rangeId: item.rangeId,
                phoneNumber: item.phoneNumber,
                workflowId: item.workflowId,
                status: item.status as JobStatus,
                users: item.users,
                invoiceId: item.invoiceId,
            };
        } else {
            document = {
                docType: 'Personal',
                id: item.id,
                userId: item.userId,
                firstName: item.userFirstName,
                lastName: item.userLastName,
                startTime: item.startTime,
                endTime: item.endTime,
                available: item.available,
                description: item.description,
            };
        }
        return document;
    });
}

export const eventStatusFilters = ['All', 'Pending', 'Canceled'] as const;
type EventStatusFilter = typeof eventStatusFilters[number];

@Injectable({
    providedIn: 'root'
})
export class ScheduleService {

    protected _type: any = '';
    itemsListFilter!: DateFilter | null;
    calendarViewFilter!: DateFilter | null;

    dateFilter$ = this.lobbyService.selectedDateRange$;
    filterAssessmentStatusSubject = new BehaviorSubject<Exclude<EventStatusFilter, 'Pending'>>('All');
    filterAssessmentStatus$ = this.filterAssessmentStatusSubject.asObservable();
    filterJobStatusSubject = new BehaviorSubject<EventStatusFilter>('All');
    filterJobStatus$ = this.filterJobStatusSubject.asObservable();

    private isLeadsViewSubject = new BehaviorSubject<boolean>(false);

    isLeadsView$ = this.isLeadsViewSubject.asObservable();

    setLeadsView(isLeadsView: boolean) {
        this.isLeadsViewSubject.next(isLeadsView);
    }

    private changeCalendarViewSubject = new Subject<CalendarView>();

    view$ = this.changeCalendarViewSubject.asObservable();
    showCalendarSubject = new BehaviorSubject(false);

    currentUser$ = this.usersService.currentUser$;

    setNewCalendarView(view: CalendarView) {
        if (view) {
            this.changeCalendarViewSubject.next(view);
        }
    }

    constructor(
        private leadsService: LeadsService,
        private assessmentsService: AssessmentsService,
        private jobsService: JobsService,
        private availabilityService: AvailabilityService,
        private estimatesService: EstimatesService,
        private invoicesService: InvoicesService,
        private notesService: NotesService,
        protected businessService: BusinessService,
        protected viewAsService: ViewAsService,
        protected paymentService: PaymentService,
        protected usersService: UsersService,
        protected lobbyService: LobbyService,
        protected supabaseService: SupabaseService,
        protected snackbar: MatSnackBar,
        protected router: Router,
        protected modalsService: ModalsService,
    ) {}

    allScheduleForCalendarViewObservable(options: PaginationOptions, pref = '') {
        return this.businessService.selectedBusiness$.pipe(
            // TODO: This can be improved by adding the changes handler to handle each change separately and not to pull the list again.
            switchMap(business => {
                return this.supabaseService.rpc<ScheduleItem[]>({
                    cName: pref + '_all_schedule_',
                    schema: business!.businessId,
                    fn: 'get_schedule',
                    tables: [
                        { table: 'lead' },
                        { table: 'lead_range' },
                        { table: 'assessment' },
                        { table: 'assessment_range' },
                        { table: 'job' },
                        { table: 'job_range' },
                        { table: 'job_user' },
                        { table: 'unavailability' },
                    ],
                    options
                });
            }),
            map(scheduleItems => {
                return scheduleToList(scheduleItems);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    async createLead(data: Required<RequestInvitationFormData>) {
        const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
        const assignees = data.assignees;
        let assigneeId = assignees.length === 0 ? null : assignees[0].id;
        if(assigneeId === currentUser.id) {
            assigneeId = null;
        }
        const notes = this.notesService.getLocalNotes();
        const imagePromises = this.notesService.prepareImagesForUpload(notes);
        await Promise.all([
            ...imagePromises,
            this.leadsService.createLead({
                inOwnerId: currentUser.id,
                inClient: data.client as MakeOptional<Client, 'salesTaxPercentage' | 'email' | 'address' | 'lastName'>,
                inJobType: data.jobType,
                inNotes: notes as Note[],
                inRanges: data.ranges,
                inAssignee: assigneeId,
            })
        ]);
        showSnackbar(this.snackbar, {
            message: "Lead assigned"
        });
    }

    async createAssessment(data: Required<RequestInvitationFormData>, fromWorkflowId?: number) {
        const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
        const assignees = data.assignees;
        let assigneeId = assignees.length === 0 ? null : assignees[0].id;
        if(assigneeId === currentUser.id) {
            assigneeId = null;
        }

        const promises: Promise<any>[] = [];
        const createData: AssessmentCreate = {
            inOwnerId: currentUser.id,
            inClient: data.client as MakeOptional<Client, 'salesTaxPercentage'>,
            inRanges: data.ranges,
            inAssignee: assigneeId,
            inJobType: data.jobType
        }

        if(fromWorkflowId !== undefined) {
            createData.inWorkflowId = fromWorkflowId;
        } else {
            const notes = this.notesService.getLocalNotes();
            const imagePromises = this.notesService.prepareImagesForUpload(notes);
            promises.push(...imagePromises);
            createData.inNotes = notes as Note[];
        }

        promises.push(this.assessmentsService.createAssessment(createData).then(assessmentId => {
            if(fromWorkflowId !== undefined) {
                this.router.navigate([`/assessments/${fromWorkflowId}/${assessmentId}`]);
            }
            return assessmentId;
        }));
        await Promise.all(promises);
    }

    async createEstimate(data: Required<RequestInvitationFormData>, fromWorkflowId?: number) {
        const currentUser = await this.currentUser$.pipe(take(1)).toPromise();

        const promises: Promise<any>[] = [];
        const createData: EstimateCreate = {
            inCreatedBy: currentUser.id,
            inClient: data.client as Client,
            inJobType: data.jobType
        }

        if(fromWorkflowId !== undefined) {
            createData.inWorkflowId = fromWorkflowId;
        } else {
            const notes = this.notesService.getLocalNotes();
            const imagePromises = this.notesService.prepareImagesForUpload(notes);
            promises.push(...imagePromises);
            createData.inNotes = notes as Note[];
        }

        promises.unshift(this.estimatesService.createEstimate(createData))
        const [ res ] = await Promise.all(promises);

        this.router.navigateByUrl(`estimates/${res.workflowId}/${res.estimateId}`);
    }

    async createInvoice(data: Required<RequestInvitationFormData>, fromWorkflowId?: number) {
        const currentUser = await this.currentUser$.pipe(take(1)).toPromise();

        const promises: Promise<any>[] = [];
        const createData: InvoiceCreate = {
            inCreatedBy: currentUser.id,
            inClient: data.client as Client,
            inJobType: data.jobType
        }

        if(fromWorkflowId !== undefined) {
            createData.inWorkflowId = fromWorkflowId;
        } else {
            const notes = this.notesService.getLocalNotes();
            const imagePromises = this.notesService.prepareImagesForUpload(notes);
            promises.push(...imagePromises);
            createData.inNotes = notes as Note[];
        }

        promises.unshift(this.invoicesService.createInvoice(createData))
        const [ res ] = await Promise.all(promises);

        this.router.navigateByUrl(`invoices/${res.workflowId}/${res.invoiceId}`);
    }

    async createJob(data: Required<RequestInvitationFormData>, fromWorkflowId?: number) {
        const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
        let assignees: number[] | null = data.assignees ? data.assignees.map(user => user.id) : null;
        if(assignees && (assignees.length === 0 || assignees.length === 1 && assignees[0] === currentUser.id))
            assignees = null;

        const promises: Promise<any>[] = [];
        const createData: JobCreate = {
            inCreatedBy: currentUser.id,
            inClient: data.client as MakeOptional<Client, 'salesTaxPercentage'>,
            inRanges: data.ranges,
            inJobUserIds: assignees,
            inJobType: data.jobType
        }

        if(fromWorkflowId !== undefined) {
            createData.inWorkflowId = fromWorkflowId;
        } else {
            const notes = this.notesService.getLocalNotes();
            const imagePromises = this.notesService.prepareImagesForUpload(notes);
            promises.push(...imagePromises);
            createData.inNotes = notes as Note[];
        }

        promises.push(
            this.jobsService.createJob(createData).then(jobId => {
                if(fromWorkflowId !== undefined) {
                    this.router.navigate([`/jobs/${fromWorkflowId}/${jobId}`]);
                }
                return jobId;
            })
        )
        await Promise.all(promises);
    }

    async createDocument(data: Required<RequestInvitationFormData>, fromWorkflowId?: number) {
        try {
            switch (data.dialogType) {
                case "Lead":
                    await this.createLead(data);
                    break;
                case "Assessment":
                    await this.createAssessment(data, fromWorkflowId);
                    break;
                case "Estimate":
                    await this.createEstimate(data, fromWorkflowId);
                    break;
                case "Invoice":
                    await this.createInvoice(data, fromWorkflowId);
                    break;
                case "Job":
                    await this.createJob(data, fromWorkflowId);
            }
            return null;
        } catch (e) {
            //TODO: Check if there are any errors that are needed to be provided here
            return {};
        }
    }

//---------------------- Tile click handlers -----------------------

    onItemEvent(event: DynamicListEvent<ScheduleTileData>) {
        switch (event.value.docType) {
            case "Lead":
                this.handleLeadEvent(event as DynamicListEvent<AssessmentTileData>);
                break;
            case "Assessment":
                this.handleAssessmentEvent(event as DynamicListEvent<AssessmentTileData>);
                break;
            case "Job":
                this.handleJobEvent(event as DynamicListEvent<JobTileData>);
                break;
            case "Personal":
                this.handleAvailabilityEvent(event as DynamicListEvent<AvailabilityTileData>);
                break;
        }
    }

    handleLeadEvent(event: DynamicListEvent<AssessmentTileData>) {
        switch(event.eventEmitterName) {
            case 'tileClick':
                this.openLead(event.value.id);
                break;
            case 'deleteLead':
                this.deleteLead(event.value.id, event.value.workflowId);
                break;
        }
    }

    handleAssessmentEvent(event: DynamicListEvent<AssessmentTileData>) {
        switch(event.eventEmitterName) {
            case 'tileClick':
                this.router.navigate(['/assessments', event.value.workflowId, event.value.id]);
                break;
            case 'deleteAssessment':
                this.deleteAssessment(event.value.id, event.value.workflowId);
                break;
        }
    }

    async deleteAssessment(assessmentId: number, workflowId: number) {
        const notes = await this.notesService.notesObservable(workflowId).pipe(take(1)).toPromise();
        const assessment = await this.assessmentsService.getAssessment(assessmentId);
        const restoreData: AssessmentRestore = {
            inId: assessment.id,
            inCreatedAt: assessment.createdAt,
            inClient: await clientRestoreFromDocument(assessment),
            inOwnerId: assessment.ownerId,
            inRanges: assessment.ranges,
            inStatus: assessment.status,
            inDenied: assessment.denied,
            inWorkflowId: assessment.workflowId,
            inJobType: assessment.jobType,
            inNotes: notes!
        };
        if(assessment.assigneeId)
            restoreData.inAssignee = assessment.assigneeId;

        this.modalsService.open(ConfirmationDialog, {
            behavior: ModalBehavior.Dialog,
            disableClose: true,
            data: {
                title: "Delete",
                message: "Are you sure you want to delete this assessment?",
                actionTitle: "Delete",
                actionColor: 'warn',
                action: async () => {
                    await this.assessmentsService.deleteAssessment(assessmentId);
                    showSnackbar(this.snackbar, {
                        message: 'Assessment deleted',
                        duration: 10000,
                        actionText: 'Undo',
                        action: async () => {
                            await this.assessmentsService.restoreAssessment(restoreData);
                        }
                    });
                }
            }
        });
    }

    async deleteLead(leadId: number, workflowId: number) {
        const notes = await this.notesService.notesObservable(workflowId).pipe(take(1)).toPromise();
        const lead = await this.leadsService.getLead(leadId);
        const clientRestore = await clientRestoreFromDocument(lead);
        const restoreData: LeadRestore = {
            inId: leadId,
            inClient: clientRestore,
            inCreatedAt: lead.createdAt,
            inJobType: lead.jobType,
            inNotes: notes!,
            inOwnerId: lead.ownerId,
            inDenied: lead.denied,
            inWorkflowId: lead.workflowId,
            inRanges: lead.ranges,
        };
        if (!restoreData.inClient.address)
            delete (restoreData as any).inClient.address;
        if(lead.assigneeId)
            restoreData.inAssignee = lead.assigneeId;

        this.modalsService.open(ConfirmationDialog, {
            data: {
                title: 'Delete',
                message: 'Are you sure you want to delete this lead?',
                actionTitle: 'Delete',
                action: async () => {
                    await this.leadsService.deleteLead(lead.id);
                    showSnackbar(this.snackbar, {
                        message: 'Lead deleted',
                        duration: 10000,
                        actionText: 'Undo',
                        action: async () => {
                            await this.leadsService.restoreLead(restoreData);
                        }
                    });
                }
            },
            behavior: ModalBehavior.Dialog,
            breakpoint: RequestInvitationModal.BREAKPOINT,
        });
    }

    async updateLeadOrCreateDocument(original: Lead, data: Required<RequestInvitationFormData>) {
        if(data.dialogType !== 'Lead') {
            await Promise.all([
                this.createDocument(data, original.workflowId),
                this.leadsService.deleteLead(original.id)
            ]);
        } else {
            const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
            const updateData: LeadUpdate = {
                inId: original.id,
                inClient: data.client as Client,
                inJobType: data.jobType,
                inAssignee: data.assignees ? data.assignees[0].id : original.assigneeId
            };
            if(data.assignees) {
                updateData.inDenied = false;
                if(updateData.inAssignee === currentUser.id)
                  updateData.inAssignee = null;
              }
            if(data.ranges)
                updateData.inRanges = data.ranges;
            await this.leadsService.updateLead(updateData);
        }
        return null;
    }

    async openLead(leadId: number) {
        const lead = await this.leadsService.getLead(leadId);
        const users = await this.usersService.users$.pipe(take(1)).toPromise();
        const readonly = await this.viewAsService.readonly$.pipe(take(1)).toPromise();
        const assignee = lead.assigneeId ?? lead.ownerId;
        const data: RequestInvitationModalData = {
            initialData: {
                id: lead.id,
                workflowId: lead.workflowId,
                dialogType: "Lead",
                jobType: lead.jobType,
                client: {
                    businessName: lead.businessName ?? undefined,
                    firstName: lead.firstName,
                    lastName: lead.lastName,
                    phoneNumber: lead.phoneNumber,
                    extNumber: lead.extNumber ?? undefined,
                    address: lead.address,
                    unit: lead.unit ?? undefined,
                    email: lead.email ?? undefined,
                    type: lead.type,
                },
                ranges: lead.ranges,
                assignees: [users!.find(user => user.id === assignee)!],
            },
            showDialogTypeChoice: true,
            readonly,
            onSubmit: ((data: Required<RequestInvitationFormData>) => this.updateLeadOrCreateDocument(lead, data)).bind(this),
        };

        await this.modalsService.open(RequestInvitationModal, {
            data,
            behavior: ModalBehavior.Auto,
            breakpoint: RequestInvitationModal.BREAKPOINT,
        });
    }

    handleJobEvent(event: DynamicListEvent<JobTileData>) {
        switch(event.eventEmitterName) {
            case 'tileClick':
                this.router.navigate(['/jobs', event.value.workflowId, event.value.id]);
                break;
            case 'deleteJob':
                this.deleteJob(event.value.id, event.value.workflowId);
                break;
        }
    }

    async deleteJob(jobId: number, workflowId: number) {
        const notes = await this.notesService.notesObservable(workflowId).pipe(take(1)).toPromise();
        const job = await this.jobsService.getJob(jobId);
        const restoreData: JobRestore = {
            inId: job.id,
            inCreatedAt: job.createdAt,
            inCreatedBy: job.createdBy,
            inClient: await clientRestoreFromDocument(job),
            inRanges: job.ranges,
            inStatus: job.status,
            inJobUsers: job.users.map(user => ({
                id: user.id,
                assigneeUserId: user.userId,
                assignedByUserId: user.assignedBy,
                acceptanceStatus: user.acceptanceStatus
            }) as JobUserRestore),
            inWorkflowId: job.workflowId,
            inJobType: job.jobType,
            inNotes: notes!
        };

        if(job.statusStoreForCancel)
            restoreData.inStatusStoreForCancel = job.statusStoreForCancel;

        this.modalsService.open(ConfirmationDialog, {
            behavior: ModalBehavior.Dialog,
            disableClose: true,
            data: {
                title: "Delete",
                message: "Are you sure you want to delete this job?",
                actionTitle: "Delete",
                actionColor: 'warn',
                action: async () => {
                    await this.jobsService.deleteJob(jobId);
                    showSnackbar(this.snackbar, {
                        message: 'Job deleted',
                        duration: 10000,
                        actionText: 'Undo',
                        action: async () => {
                            return this.jobsService.restoreJob(restoreData);
                        }
                    });
                }
            }
        });
    }

    handleAvailabilityEvent(event: DynamicListEvent<AvailabilityTileData>) {
        switch(event.eventEmitterName) {
            case 'tileClick':
                const date = event.value.startTime
                this.openAvailabilityDialogWithSelectedDate(date);
                break;
        }
    }

    openAvailabilityDialogWithSelectedDate(date: Date) {
        this.modalsService.open(TimetableDialogComponent, {
            behavior: ModalBehavior.Dialog,
            disableClose: true,
            autoFocus: false,
            restoreFocus: false,
            data: {
                setUnavailableTab: true,
                selectedDate: date
            }
        })
    }

//---------------------- Realtime events handlers -----------------------

    private async jobFulfillsFilters(job: Job, rangeId: number | null = null, currentStatusFilter: EventStatusFilter, handleDateFilter = false) {
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        const range = rangeId === null ? null : (job.ranges.find(r => r.rangeId === rangeId) ?? null);
        return (
                currentStatusFilter === 'All' || 
                currentStatusFilter === 'Canceled' && job.status === 'canceled' || 
                currentStatusFilter === 'Pending' && job.status === 'pending'
            ) &&
            (!userIds || job.users.some(user => userIds.includes(user.userId) && ['accepted', 'looped_in'].includes(user.acceptanceStatus))) &&
            (!handleDateFilter || range === null || handleDateFilter && dateFilter && moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'));
    }

    private async leadFulfillsFilters(lead: Lead, rangeId: number | null = null, handleDateFilter = false) {
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        const range = rangeId === null ? null : (lead.ranges.find(r => r.rangeId === rangeId) ?? null);
        return (!userIds || userIds.includes(lead.ownerId)) &&
            !lead.assigneeId &&
            (!handleDateFilter || range === null || handleDateFilter && dateFilter && moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'));
    }

    private async assessmentFulfillsFilters(assessment: Assessment, rangeId: number | null = null, currentStatusFilter: Exclude<EventStatusFilter, 'Pending'>, handleDateFilter = false) {
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        const range = rangeId === null ? null : (assessment.ranges.find(r => r.rangeId === rangeId) ?? null);
        return (
                currentStatusFilter === 'All' || 
                currentStatusFilter === 'Canceled' && assessment.status === 'canceled'
            ) &&
            !assessment.assigneeId &&
            (!userIds || userIds.includes(assessment.ownerId)) &&
            (!handleDateFilter || range === null || handleDateFilter && dateFilter && moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'));
    }

    private async unavailableFulfillsFilters(unavailable: Availability, handleDateFilter = false) {
        const userIds = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        return (!userIds || userIds.includes(unavailable.userId)) &&
            (!handleDateFilter || handleDateFilter && dateFilter && moment(unavailable.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'));
    }

    private async insertLead(
        list: DynamicListComponent<LeadTileComponent, any>, 
        lead: Lead, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        range: TimeRange | null = null, 
        isDateFilterList = false,
        showDocType = false
    ) {
        const insertRange = async (range: TimeRange) => {
            const rangeId = range.rangeId ?? range.id;
            const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
            const lastComponent = list.getLastComponent();
            const firstComponent = list.getFirstComponent();
            if (
                list.includesItem((_, item) =>
                    item?.instance.document.docType === 'Lead' && 
                    item?.instance.document.id === lead.id && 
                    item.instance.document.rangeId === rangeId
                ) ||
                isDateFilterList && dateFilter && !moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]') ||
                list.canLoadMoreBottom && lastComponent && range.startTime > lastComponent.instance.document.startTime || 
                list.canLoadMoreTop && firstComponent && range.startTime < firstComponent.instance.document.startTime
            )
                return;

            const now = new Date();
            list.insertItem(
                (prev, current) => {
                    if (!prev && range.startTime < current.instance.document.startTime)
                        return true;

                    return prev ? moment(range.startTime).isBetween(prev.instance.document.startTime, current.instance.document.startTime, undefined, '[]') : false;
                },
                {
                    componentClass: LeadTileComponent,
                    header: dateString(range.startTime, range.endTime),
                    args: { document: leadTileData(lead, rangeId!), showDocType }
                },
                range.endTime > now ? 'bottom' : 'top',
                range.endTime > now ? !list.canLoadMoreBottom : !list.canLoadMoreTop
            );
            if (range.endTime > now) {
                if (!firstHeaderRangeSubject.value || range.startTime <= firstHeaderRangeSubject.value.startTime) {
                    firstHeaderRangeSubject.next({ startTime: range.startTime, endTime: range.endTime });
                }
            }
        }
        
        if (range === null) {
            for (const range of lead.ranges)
                await insertRange(range);
            return;
        }
        await insertRange(range);
    }
    
    private async insertJob(
        list: DynamicListComponent<JobTileComponent, any>, 
        job: Job, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        range: TimeRange | null = null, 
        isDateFilterList = false,
        showDocType = false
    ) {
        const insertRange = async (range: TimeRange) => {
            const rangeId = range.rangeId ?? range.id;
            const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
            const lastComponent = list.getLastComponent();
            const firstComponent = list.getFirstComponent();
            if (
                list.includesItem((_, item) => 
                    item?.instance.document.docType === 'Job' && 
                    item?.instance.document.id === job.id &&
                    // !!item.instance.document.users.find(u => u.userId === user.userId) &&
                    item.instance.document.rangeId === rangeId
                ) ||
                isDateFilterList && dateFilter && !moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]') ||
                list.canLoadMoreBottom && lastComponent && range.startTime > lastComponent.instance.document.startTime || 
                list.canLoadMoreTop && firstComponent && range.startTime < firstComponent.instance.document.startTime
            )
                return;

            const now = new Date();
            list.insertItem(
                (prev, current) => {
                    if (!prev && range.startTime < current.instance.document.startTime)
                        return true;

                    return prev ? moment(range.startTime).isBetween(prev.instance.document.startTime, current.instance.document.startTime, undefined, '[]') : false;
                },
                {
                    componentClass: JobTileComponent,
                    header: dateString(range.startTime, range.endTime),
                    args: { document: jobTileData(job, rangeId!), showDocType }
                },
                range.endTime > now ? 'bottom' : 'top',
                range.endTime > now ? !list.canLoadMoreBottom : !list.canLoadMoreTop
            );
            if (range.endTime > new Date()) {
                if (!firstHeaderRangeSubject.value || range.startTime <= firstHeaderRangeSubject.value.startTime) {
                    firstHeaderRangeSubject.next({ startTime: range.startTime, endTime: range.endTime });
                }
            }
        }

        if (range === null) {
            for (const range of job.ranges)
                await insertRange(range);
            return;
        }
        await insertRange(range);
    }

    private async insertAssessment(
        list: DynamicListComponent<AssessmentTileComponent, any>, 
        assessment: Assessment, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        range: TimeRange | null = null, 
        isDateFilterList = false,
        showDocType = false
    ) {
        const insertRange = async (range: TimeRange) => {
            const rangeId = range.rangeId ?? range.id;
            const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
            const lastComponent = list.getLastComponent();
            const firstComponent = list.getFirstComponent();
            if (
                list.includesItem((_, item) =>
                    item?.instance.document.docType === 'Assessment' && 
                    item?.instance.document.id === assessment.id && 
                    item.instance.document.rangeId === rangeId
                ) ||
                isDateFilterList && dateFilter && !moment(range.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]') ||
                list.canLoadMoreBottom && lastComponent && range.startTime > lastComponent.instance.document.startTime || 
                list.canLoadMoreTop && firstComponent && range.startTime < firstComponent.instance.document.startTime
            )
                return;

            const now = new Date();
            list.insertItem(
                (prev, current) => {
                    if (!prev && range.startTime < current.instance.document.startTime)
                        return true;

                    return prev ? moment(range.startTime).isBetween(prev.instance.document.startTime, current.instance.document.startTime, undefined, '[]') : false;
                },
                {
                    componentClass: AssessmentTileComponent,
                    header: dateString(range.startTime, range.endTime),
                    args: { document: assessmentTileData(assessment, rangeId!), showDocType }
                },
                range.endTime > now ? 'bottom' : 'top',
                range.endTime > now ? !list.canLoadMoreBottom : !list.canLoadMoreTop
            );
            if (range.endTime > new Date()) {
                if (!firstHeaderRangeSubject.value || range.startTime <= firstHeaderRangeSubject.value.startTime) {
                    firstHeaderRangeSubject.next({ startTime: range.startTime, endTime: range.endTime });
                }
            }
        }
        
        if (range === null) {
            for (const range of assessment.ranges)
                await insertRange(range);
            return;
        }
        await insertRange(range);
    }

    private async insertUnavailable(
        list: DynamicListComponent<AvailabilityTileComponent, any>, 
        unavailable: Availability, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        isDateFilterList = false,
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        const lastComponent = list.getLastComponent();
        const firstComponent = list.getFirstComponent();
        if (
            list.includesItem((_, item) => item?.instance.document.docType === 'Personal' && item.instance.document.id === unavailable.id) ||
            isDateFilterList && dateFilter && !moment(unavailable.startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]') ||
            list.canLoadMoreBottom && lastComponent && unavailable.startTime > lastComponent.instance.document.startTime || 
            list.canLoadMoreTop && firstComponent && unavailable.startTime < firstComponent.instance.document.startTime
        )
            return;

        const now = new Date();
        list.insertItem(
            (prev, current) => {
                if (!prev && unavailable.startTime < current.instance.document.startTime)
                    return true;

                return prev ? moment(unavailable.startTime).isBetween(prev.instance.document.startTime, current.instance.document.startTime, undefined, '[]') : false;
            },
            {
                componentClass: AvailabilityTileComponent,
                header: dateString(unavailable.startTime, unavailable.endTime),
                args: { document: { ...unavailable, docType: 'Personal' }, showDocType }
            },
            unavailable.endTime > now ? 'bottom' : 'top',
            unavailable.endTime > now ? !list.canLoadMoreBottom : !list.canLoadMoreTop
        );
        if (unavailable.endTime > new Date()) {
            if (!firstHeaderRangeSubject.value || unavailable.startTime <= firstHeaderRangeSubject.value.startTime) {
                firstHeaderRangeSubject.next({ startTime: unavailable.startTime, endTime: unavailable.endTime });
            }
        }
    }

    private commonRemoveEvent(
        list: DynamicListComponent<LeadTileComponent | JobTileComponent | AssessmentTileComponent | AvailabilityTileComponent>, 
        predicate: Predicate<any>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        docType: 'Lead' | 'Job' | 'Assessment' | 'Personal'
    ) {
        const result = list.removeItem('auto', (v, i) => v.instance.document.docType === docType ? predicate(v, i) : false);
        if (!firstHeaderRangeSubject?.value)
            return result;
        const item = list.getItem(item => item.instance.document.startTime > new Date()) as JobTileComponent | AssessmentTileComponent | LeadTileComponent | AvailabilityTileComponent;
        firstHeaderRangeSubject.next(item ? { startTime: item.document.startTime, endTime: item.document.endTime } : null);
        return result;
    }

    private commonRemoveEvents(
        list: DynamicListComponent<LeadTileComponent | JobTileComponent | AssessmentTileComponent | AvailabilityTileComponent>, 
        predicate: Predicate<any>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        docType: 'Lead' | 'Job' | 'Assessment' | 'Personal'
    ) {
        let found = false;
        do {
            found = this.commonRemoveEvent(list, predicate, firstHeaderRangeSubject, docType);
        } while (found);
    }

    private commonHandleUpdate(
        list: DynamicListComponent<LeadTileComponent | JobTileComponent | AssessmentTileComponent | AvailabilityTileComponent>, 
        docType: ('Lead' | 'Job' | 'Assessment' | 'Personal') | ('Lead' | 'Job' | 'Assessment' | 'Personal')[],
        data: any,
        dataIdProp: string,
        tileIdProp = 'id'
    ) {
        list.updateItems(component => {
            const doc = component.instance.document;
            if (Array.isArray(docType) && !docType.includes(doc.docType) || !Array.isArray(docType) && doc.docType !== docType)
                return false;

            if ((doc as any)[tileIdProp] === data[dataIdProp]) {
                for (const prop in doc) {
                    if (prop === 'type' && !['business', 'personal'].includes(data[prop]))
                        continue;
                    if (prop in data && !(['id', 'createdAt'].includes(prop)))
                        (doc as any)[prop] = data[prop];
                }
                component.instance.refreshData();
            }
            return false;
        });
    }

    private async commonHandleJobRangeChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<JobTimeRange>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: EventStatusFilter,
        handleDateFilter = false,
        showDocType = false
    ) {
        if (changes.eventType === 'DELETE') {
            this.commonRemoveEvents(list, item => item.instance.document.rangeId === changes.old.id, firstHeaderRangeSubject, 'Job');
            return;
        }
        const range = changes.new;
        const job = await this.jobsService.getJob(range.jobId);

        if (changes.eventType === 'UPDATE') {
            this.commonRemoveEvent(list, item => item.instance.document.rangeId === range.id, firstHeaderRangeSubject, 'Job');
        }
        if (await this.jobFulfillsFilters(job, range.id, currentStatusFilter, handleDateFilter)) {
            await this.insertJob(list, job, firstHeaderRangeSubject, range, handleDateFilter, showDocType);
        }
    }

    private async commonHandleLeadRangeChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<LeadTimeRange>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        handleDateFilter = false,
        showDocType = false
    ) {
        if (changes.eventType === 'DELETE') {
            this.commonRemoveEvent(list, item => item.instance.document.rangeId === changes.old.id, firstHeaderRangeSubject, 'Lead');
            return;
        }
        const range = changes.new;
        const lead = await this.leadsService.getLead(range.leadId);

        if (changes.eventType === 'UPDATE') {
            this.commonRemoveEvent(list, item => item.instance.document.rangeId === range.id, firstHeaderRangeSubject, 'Lead');
        }
        if (await this.leadFulfillsFilters(lead, range.id, handleDateFilter)) {
            await this.insertLead(list, lead, firstHeaderRangeSubject, range, handleDateFilter, showDocType);
        }
    }

    private async commonHandleAssessmentRangeChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<AssessmentTimeRange>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'>,
        handleDateFilter = false,
        showDocType = false
    ) {
        if (changes.eventType === 'DELETE') {
            this.commonRemoveEvent(list, item => item.instance.document.rangeId === changes.old.id, firstHeaderRangeSubject, 'Assessment');
            return;
        }
        const range = changes.new;
        const assessment = await this.assessmentsService.getAssessment(range.assessmentId);

        if (changes.eventType === 'UPDATE') {
            this.commonRemoveEvent(list, item => item.instance.document.rangeId === range.id, firstHeaderRangeSubject, 'Assessment');
        }
        if (await this.assessmentFulfillsFilters(assessment, range.id, currentStatusFilter, handleDateFilter)) {
            await this.insertAssessment(list, assessment, firstHeaderRangeSubject, range, handleDateFilter, showDocType);
        }
    }

    private async commonHandleLeadChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<Lead>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        handleDateFilter = false,
        showDocType = false
    ) {
        const leadId = (changes.new as Lead).id;
        const lead = await this.leadsService.getLead(leadId);
        const fulfillsFilters = await this.leadFulfillsFilters(lead, null, handleDateFilter);

        if (!fulfillsFilters) {
            this.commonRemoveEvents(list, item => item?.instance.document.id === leadId, firstHeaderRangeSubject, 'Lead');
            return;
        } else if (fulfillsFilters && !list.includesItem((_, item) => item?.instance.document.id === leadId)) {
            await this.insertLead(list, lead, firstHeaderRangeSubject, null, handleDateFilter, showDocType);
            return;
        }
        this.commonHandleUpdate(list, 'Lead', changes.new, 'id');
    }

    private async commonHandleJobChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<Job>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: EventStatusFilter,
        handleDateFilter = false,
        showDocType = false
    ) {
        const jobId = (changes.new as Job).id;
        const job = await this.jobsService.getJob(jobId);
        const fulfillsFilters = await this.jobFulfillsFilters(job, null, currentStatusFilter, handleDateFilter);

        if (!fulfillsFilters) {
            this.commonRemoveEvents(list, item => item?.instance.document.id === jobId, firstHeaderRangeSubject, 'Job');
            return;
        } else if (fulfillsFilters && !list.includesItem((_, item) => item?.instance.document.id === jobId)) {
            await this.insertJob(list, job, firstHeaderRangeSubject, null, handleDateFilter, showDocType);
            return;
        }
        this.commonHandleUpdate(list, 'Job', changes.new, 'id');
    }

    private async commonHandleAssessmentChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<Assessment>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'>,
        handleDateFilter = false,
        showDocType = false
    ) {
        const assessmentId = (changes.new as Assessment).id;
        const assessment = await this.assessmentsService.getAssessment(assessmentId);
        const fulfillsFilters = await this.assessmentFulfillsFilters(assessment, null, currentStatusFilter, handleDateFilter);

        if (!fulfillsFilters) {
            this.commonRemoveEvents(list, item => item?.instance.document.id === assessmentId, firstHeaderRangeSubject, 'Assessment');
            return;
        } else if (fulfillsFilters && !list.includesItem((_, item) => item?.instance.document.id === assessmentId)) {
            await this.insertAssessment(list, assessment, firstHeaderRangeSubject, null, handleDateFilter, showDocType);
            return;
        }
        this.commonHandleUpdate(list, 'Assessment', changes.new, 'id');
    }

    private async commonHandleJobUserChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<any>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: EventStatusFilter,
        handleDateFilter = false,
        showDocType = false
    ) {
        const workflowId = changes.new.workflowId;
        const acceptanceStatus = changes.new.acceptanceStatus;
        if (
            (changes.eventType === 'INSERT' || changes.eventType === 'UPDATE') && 
            (acceptanceStatus === 'accepted' || acceptanceStatus === 'looped_in') && 
            !list.includesItem((_, item) => item?.instance.document.workflowId === workflowId)
        ) {
            const job = await this.jobsService.getJob(changes.new.jobId);
            if (await this.jobFulfillsFilters(job, null, currentStatusFilter, handleDateFilter)) {
                await this.insertJob(list, job, firstHeaderRangeSubject, null, handleDateFilter, showDocType);
            }
        } else if (changes.eventType === 'DELETE') {
            this.commonRemoveEvents(list, item => item.instance.document.jobUserId === changes.old.id, firstHeaderRangeSubject, 'Job');
        }
    }
    
    private async commonHandleUnavailabilityChanges(
        list: DynamicListComponent<AvailabilityTileComponent>, 
        changes: RealtimePostgresChangesPayload<Availability>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        handleDateFilter = false, 
        showDocType = false
    ) {
        if (changes.eventType === 'DELETE') {
            this.commonRemoveEvent(list, item => item.instance.document.id === changes.old.id, firstHeaderRangeSubject, 'Personal');
            return;
        }
        const unavailable = changes.new;

        if (changes.eventType === 'UPDATE') {
            if (list.includesItem((_, item) => {
                    const doc = item.instance.document;
                    return doc.docType === 'Personal' &&
                        doc.id === changes.new.id && 
                        moment(doc.startTime).isSame(changes.new.startTime) &&
                        moment(doc.endTime).isSame(changes.new.endTime);
                })
            ) {
                this.commonHandleUpdate(list, 'Personal', changes.new, 'id');
                return;
            }
            this.commonRemoveEvent(list, item => item.instance.document.id === unavailable.id, firstHeaderRangeSubject, 'Personal');
        }
        if (await this.unavailableFulfillsFilters(unavailable, handleDateFilter)) {
            const new_unavailable = await this.availabilityService.getUnavailability(unavailable.id);
            await this.insertUnavailable(list, new_unavailable, firstHeaderRangeSubject, handleDateFilter, showDocType);
        }
    }

    handleJobChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<Job>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        this.commonHandleJobChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, false, showDocType);
    }

    handleJobClientChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        this.commonHandleUpdate(list, 'Job', changes.new, 'jobId');
    }

    handleJobRangeChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<JobTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        this.commonHandleJobRangeChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, false, showDocType);
    }

    handleWorkflowChanges(
        list: DynamicListComponent<LeadTileComponent | JobTileComponent | AssessmentTileComponent>,
        changes: RealtimePostgresChangesPayload<Workflow>
    ) {
        this.commonHandleUpdate(list, ['Lead', 'Assessment', 'Job'], changes.new, 'id', 'workflowId');
    }

    handleJobUserChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<any>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        this.commonHandleJobUserChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, false, showDocType);
    }

    async handleDateFilterJobChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<Job>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleJobChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, true, showDocType);
    }

    async handleDateFilterJobClientChanges(
        list: DynamicListComponent<JobTileComponent>,
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleUpdate(list, 'Job', changes.new, 'jobId');
    }

    async handleDateFilterJobRangeChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<JobTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter || changes.eventType === 'INSERT' && !moment((changes.new as any).startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'))
            return;
        this.commonHandleJobRangeChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, true, showDocType);
    }

    async handleDateFilterWorkflowChanges(
        list: DynamicListComponent<LeadTileComponent | JobTileComponent | AssessmentTileComponent>,
        changes: RealtimePostgresChangesPayload<Workflow>
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleUpdate(list, ['Lead', 'Assessment', 'Job'], changes.new, 'workflowId');
    }

    async handleDateFilterJobUserChanges(
        list: DynamicListComponent<JobTileComponent>, 
        changes: RealtimePostgresChangesPayload<any>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>, 
        currentStatusFilter: EventStatusFilter = 'All',
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleJobUserChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, true, showDocType);
    }

    async handlePaymentChanges(changes: RealtimePostgresChangesPayload<any>, list: DynamicListComponent<JobTileComponent>) {
        const workflowId = (changes.new as Job).workflowId;
        const jobId = (list.getItem(item => item.instance.document.workflowId === workflowId) as JobTileComponent | null)?.document.id;
        if (!jobId)
            return;
        const job = await this.jobsService.getJob(jobId);
        this.commonHandleUpdate(list, 'Job', job, 'id');
    }

    handleLeadChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<Lead>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        this.commonHandleLeadChanges(list, changes, firstHeaderRangeSubject, false, showDocType);
    }

    handleLeadClientChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        this.commonHandleUpdate(list, 'Lead', changes.new, 'leadId');
    }

    handleLeadRangeChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<LeadTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        this.commonHandleLeadRangeChanges(list, changes, firstHeaderRangeSubject, false, showDocType);
    }

    async handleDateFilterLeadChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<Lead>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleLeadChanges(list, changes, firstHeaderRangeSubject, true, showDocType);
    }

    async handleDateFilterLeadClientChanges(
        list: DynamicListComponent<LeadTileComponent>,
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleUpdate(list, 'Lead', changes.new, 'leadId');
    }

    async handleDateFilterLeadRangeChanges(
        list: DynamicListComponent<LeadTileComponent>, 
        changes: RealtimePostgresChangesPayload<LeadTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter || changes.eventType === 'INSERT' && !moment((changes.new as any).startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'))
            return;
        this.commonHandleLeadRangeChanges(list, changes, firstHeaderRangeSubject, true, showDocType);
    }

    handleAssessmentChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<Assessment>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'> = 'All',
        showDocType = false
    ) {
        this.commonHandleAssessmentChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, false, showDocType);
    }

    handleAssessmentClientChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        this.commonHandleUpdate(list, 'Assessment', changes.new, 'assessmentId');
    }

    handleAssessmentRangeChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<AssessmentTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'> = 'All',
        showDocType = false
    ) {
        this.commonHandleAssessmentRangeChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, false, showDocType);
    }

    async handleDateFilterAssessmentChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<Assessment>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'> = 'All',
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleAssessmentChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, true, showDocType);
    }

    async handleDateFilterAssessmentClientChanges(
        list: DynamicListComponent<AssessmentTileComponent>,
        changes: RealtimePostgresChangesPayload<Client>
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter)
            return;
        this.commonHandleUpdate(list, 'Assessment', changes.new, 'assessmentId');
    }

    async handleDateFilterAssessmentRangeChanges(
        list: DynamicListComponent<AssessmentTileComponent>, 
        changes: RealtimePostgresChangesPayload<AssessmentTimeRange>,
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        currentStatusFilter: Exclude<EventStatusFilter, 'Pending'> = 'All',
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter || changes.eventType === 'INSERT' && !moment((changes.new as any).startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'))
            return;
        this.commonHandleAssessmentRangeChanges(list, changes, firstHeaderRangeSubject, currentStatusFilter, true, showDocType);
    }

    handleUnavailabilityChanges(
        list: DynamicListComponent<AvailabilityTileComponent>, 
        changes: RealtimePostgresChangesPayload<Availability>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        this.commonHandleUnavailabilityChanges(list, changes, firstHeaderRangeSubject, false, showDocType);
    }

    async handleDateFilterUnavailabilityChanges(
        list: DynamicListComponent<AvailabilityTileComponent>, 
        changes: RealtimePostgresChangesPayload<Availability>, 
        firstHeaderRangeSubject: BehaviorSubject<TimeRange | null>,
        showDocType = false
    ) {
        const dateFilter = await this.dateFilter$.pipe(take(1)).toPromise();
        if (!dateFilter || changes.eventType === 'INSERT' && !moment((changes.new as any).startTime).isBetween(dateFilter.from, dateFilter.to, undefined, '[]'))
            return;
        this.commonHandleUnavailabilityChanges(list, changes, firstHeaderRangeSubject, true, showDocType);
    }

}
